import { colord } from "colord";
import { ALPHA, EYEDROPPER, COLOR, HUE } from "@showitinc/color-picker";
import UndoRedoManager from "../managers/UndoRedoManager";
import { MIXED } from "../constants/DesignConstants.mjs";

import AppConstants from "../constants/AppConstants";

const { INPUT_SOURCE_PROPERTY_INPUT, TRANSPARENT } = AppConstants;

const _isMixed = (e, originalValue) =>
  isColorEvent(e) &&
  parseColorEvent(e.detail, originalValue) === MIXED &&
  e?.detail?.changed !== ALPHA;

const _formatSIChange =
  (propNameGetter) =>
  (
    propName,
    originalValue,
    addUndo = true,
    useGetter = true,
    defaultValue = undefined,
    action = undefined
  ) =>
  (e) => {
    if (_isMixed(e, originalValue)) return {};

    const propInfo = {
      addUndo: isColorEvent(e) && e?.detail.changed === EYEDROPPER ? true : addUndo,
      name: useGetter && propNameGetter ? propNameGetter(propName) : propName,
      origValue: originalValue,
      source: INPUT_SOURCE_PROPERTY_INPUT,
      value: isColorEvent(e)
        ? parseColorEvent(e.detail, originalValue)
        : e?.detail?.value !== undefined
        ? e.detail.value
        : e,
      defaultValue,
      action,
    };

    return propInfo;
  };

/**
 * Like the initialized _formatSIChange, but the formatted prop change will be fed immediately to onPropChange
 * This is the most typical usage.
 * @param {*} formatter _formatSIChange with a propNameGetter.
 * @param {*} onPropChange A function that consumes a prop update.
 * @returns A function ready to consume an SI prop change custom event, or straight value.
 */
const _formattedPropChange =
  (onPropChange, formatter) =>
  /** This is what is exposed */
  /**
   *
   * @param {string} propName - base prop name, the getter should ideally add conditional pathing
   * @param {any?} originalValue - value before change
   * @param {boolean} addUndo - defaults to true
   * @param {boolean} useGetter - use getter, else just use prop name as is
   * @param {any} defaultValue - a default value passed through the propUpdate to be picked up by the formatter for later manipulation in a more programmatic way
   * @returns
   */
  (propName, originalValue, addUndo, useGetter, defaultValue, action) => {
    const initializedFormatter = formatter(
      propName,
      originalValue,
      addUndo,
      useGetter,
      defaultValue,
      action
    );

    return (e) => {
      // add save point without pushing current value to onChange. Prevents a SiteDesignStore doing an unnecessary trigger. Might be a better spot for this.
      if (e?.type === "si-before-change") {
        UndoRedoManager.save();
        return;
      }
      onPropChange(initializedFormatter(e));
    };
  };

/**
 * Exposes the formatter prepped with onPropChange and a propNameGetter, which can be undefined if the propname is to be used as is.
 * Also exposes the most typical prop change usage as formattedOnPropChange, and a version expected to be fed into a color picker component.
 * @param {*} propNameGetter
 * @param {*} onPropChange
 * @returns
 */
// eslint-disable-next-line
export const initFormatter = (
  onPropChange,
  propNameGetter = (propName) => propName,
  parentPropName = undefined
) => {
  if (!onPropChange) {
    throw new Error("onPropChange must be supplied as the first argument to initFormatter");
  }

  const formatter = _formatSIChange(
    parentPropName ? (propName) => propNameGetter(propName, parentPropName) : propNameGetter
  );
  const formatPropChange = _formattedPropChange(onPropChange, formatter);

  return {
    formatter,
    onChange: formatPropChange,
  };
};

const isColorEvent = (e) => {
  if (!e?.detail) return false;
  const { changed } = e?.detail;
  return [ALPHA, COLOR, EYEDROPPER, HUE].includes(changed);
};

const parseColorEvent = (detail, originalValue) => {
  const { changed, color, alpha } = detail;
  if (changed === ALPHA) {
    const [colorPart] = String(originalValue || TRANSPARENT).split(":");
    return Number(alpha) === 100 ? colorPart : `${colorPart}:${alpha}`;
  }
  const { r, g, b } = color.rgba;
  return `${colord({ r, g, b }).toHex()}:${alpha}`;
};
